import { useMemo, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { AlertTriangle, Link2, Undo2 } from 'lucide-react'
import { getErrorMessage } from '@/api/errors'
import { qk } from '@/api/query-keys'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { PROJECT_TYPE_LABEL } from '@/components/composites/status-badge'
import { cn } from '@/lib/utils'
import { lifecycleApi } from './api'
import { PHASE_LABEL, type MigrationGroup, type MigrationResult } from './model'

/**
 * Moving the projects from before Client Projects onto them, with the person deciding:
 * - which projects move (all ticked to start; an unticked one is not touched at all);
 * - whether two differently-named client records are the same company ("CNX" and
 *   "Concentrix") — never assumed, only suggested;
 * - what a new Client Project is called.
 * Nothing changes until the summary is confirmed. Nothing is deleted.
 */

const OWN = '__own__'
const NEW = '__new__'
const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })

/** "Concentrix Philippines" → "concentrix philippines"; "C.N.X." → "cnx". */
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]+/g, '').replace(/\s+/g, ' ').trim()
const STOP = new Set(['inc', 'corp', 'corporation', 'ltd', 'llc', 'co', 'company', 'the', 'and', 'of', 'group', 'pte', 'pvt', 'limited', 'holdings'])
const words = (s: string) => norm(s).split(' ').filter((w) => w && !STOP.has(w))
const acronym = (s: string) => words(s).map((w) => w[0]).join('')

/** Whether two client names could be one company: an acronym of the other, the same first word, or one inside the other. */
export function looksSame(a: string, b: string): boolean {
  const na = words(a).join(' ')
  const nb = words(b).join(' ')
  if (!na || !nb || na === nb) return Boolean(na) && na === nb
  const compact = (s: string) => s.replace(/ /g, '')
  if (acronym(a).length >= 2 && (acronym(a) === compact(nb) || acronym(b) === compact(na))) return true
  const fa = words(a)[0]!
  const fb = words(b)[0]!
  if (fa.length >= 3 && fa === fb) return true
  return (na.length >= 3 && nb.includes(na)) || (nb.length >= 3 && na.includes(nb))
}

const clientOf = (g: MigrationGroup) => g.convert.find((c) => `${c.clientId}|${c.geographyId ?? ''}` === g.key)?.clientName || g.name.split(' - ')[0]!

export function MigrateDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [sameAs, setSameAs] = useState<Record<string, string>>({})
  const [excluded, setExcluded] = useState<Set<string>>(() => new Set())
  const [names, setNames] = useState<Record<string, string>>({})
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set())
  const [main, setMain] = useState<Record<string, string>>({})
  const [order, setOrder] = useState<Record<string, number>>({})
  const [step, setStep] = useState<'choose' | 'confirm'>('choose')
  const [result, setResult] = useState<MigrationResult | null>(null)

  // What was chosen per client: a project id, or NEW for "open a new one". No answer lets the server default
  // (a client with a single project makes that project the Client Project itself).
  const mainIds = useMemo(() => Object.fromEntries(Object.entries(main).map(([k, v]) => [k, v === NEW ? 'new' : v])), [main])
  const preview = useQuery({
    queryKey: qk.lifecycleMigration(JSON.stringify({ sameAs, main: mainIds })),
    queryFn: () => lifecycleApi.migrate({ dryRun: true, sameAs, main: mainIds }),
    staleTime: 0,
    gcTime: 0,
    placeholderData: keepPreviousData,
  })
  const groups = useMemo(() => preview.data?.groups ?? [], [preview.data])
  const idsOf = (g: MigrationGroup) => [...(g.mainId ? [g.mainId] : []), ...g.convert.map((c) => c.id)]
  const selectedIds = groups.flatMap(idsOf).filter((id) => !excluded.has(id))
  const live = groups.filter((g) => idsOf(g).some((id) => !excluded.has(id)))
  const mainKept = (g: MigrationGroup) => (g.mainId && !excluded.has(g.mainId) ? g.mainId : null)
  /** A group's additional requirements in the order the person set, numbers as typed. */
  const ordered = (g: MigrationGroup) =>
    [...g.convert]
      .map((c) => ({ ...c, n: order[c.id] ?? c.order }))
      .sort((a, b) => a.n - b.n || a.order - b.order)

  const run = useMutation({
    mutationFn: () =>
      lifecycleApi.migrate({
        dryRun: false,
        sameAs,
        selected: selectedIds,
        // Every answer made explicit, so the move does exactly what the preview showed.
        main: Object.fromEntries(live.filter((g) => !g.existingId).map((g) => [g.key, mainKept(g) ?? 'new'])),
        // The numbers as shown, renumbered 1…n per group so gaps and ties cannot surprise anyone.
        order: Object.fromEntries(groups.flatMap((g) => ordered(g).filter((c) => !excluded.has(c.id)).map((c, i) => [c.id, i + 1] as const))),
        names: Object.fromEntries(Object.entries(names).filter(([k, v]) => v.trim() && groups.some((g) => g.key === k && !g.existingId))),
      }),
    retry: false,
    onSuccess: (r) => {
      setResult(r)
      void qc.invalidateQueries({ queryKey: qk.projects() })
      void qc.invalidateQueries({ queryKey: qk.lifecycles() })
      if (r.failed?.length) toast.error(`${r.failed.length} could not be moved; the rest were.`)
      else toast.success(`${r.done.length} Client Project${r.done.length === 1 ? '' : 's'} set up`)
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  })

  const toggle = (ids: string[], on: boolean) =>
    setExcluded((x) => {
      const next = new Set(x)
      for (const id of ids) {
        if (on) next.delete(id)
        else next.add(id)
      }
      return next
    })
  const join = (from: string, to: string | null) =>
    setSameAs((m) => {
      const next = { ...m }
      if (to) next[from] = to
      else delete next[from]
      // Anything that pointed at `from` now follows it.
      for (const [k, v] of Object.entries(next)) if (v === from && to) next[k] = to
      return next
    })

  if (result) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Projects moved</DialogTitle>
            <DialogDescription>{result.done.length} Client Project{result.done.length === 1 ? '' : 's'} set up. Unticked projects were left as they were.</DialogDescription>
          </DialogHeader>
          {result.failed?.length ? (
            <ul className="grid gap-1 text-sm text-danger">{result.failed.map((f) => <li key={f.key}>{f.name}: {f.error}</li>)}</ul>
          ) : null}
          <DialogFooter><Button onClick={onClose}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !run.isPending && onClose()}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{step === 'confirm' ? 'Confirm the move' : 'Move existing projects to Client Projects'}</DialogTitle>
          <DialogDescription>
            One Client Project per client and region. Tick the projects to move; unticked ones are left exactly as they are. If two clients below are the same company under different
            names, say so and they share one Client Project. Every moved project keeps its work items and becomes an Additional Requirements project. Nothing is deleted.
          </DialogDescription>
        </DialogHeader>

        {preview.isPending ? (
          <LoadingState label="Working out the plan…" />
        ) : preview.error ? (
          <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
        ) : !groups.length ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Nothing to move — every client project is already set up.</p>
        ) : step === 'confirm' ? (
          <div className="grid max-h-[60vh] gap-2 overflow-y-auto text-sm">
            {live.map((g) => {
              const moving = ordered(g).filter((c) => !excluded.has(c.id))
              const kept = mainKept(g)
              const chosen = kept ? g.candidates.find((c) => c.id === kept) : undefined
              return (
                <div key={g.key} className="rounded-lg border border-border px-3 py-2">
                  <p className="font-medium">
                    {g.existingId ? g.name : names[g.key]?.trim() || g.name}{' '}
                    <span className="font-normal text-muted-foreground">{g.existingId ? '· already a Client Project' : chosen ? `· in ${PHASE_LABEL[g.phase]}` : '· new Client Project'}</span>
                  </p>
                  {chosen ? <p className="text-xs text-muted-foreground">“{chosen.name}” becomes the Client Project itself, renamed, keeping its work items.</p> : null}
                  {moving.length ? (
                    <p className="text-xs text-muted-foreground">Additional Requirements, in order: {moving.map((c, i) => `${i + 1}. ${c.name}`).join(' · ')}</p>
                  ) : null}
                  {g.joined.length ? (
                    <p className="text-xs text-warning">Joined as the same client: {[...new Set(moving.filter((c) => `${c.clientId}|${c.geographyId ?? ''}` !== g.key).map((c) => c.clientName))].join(', ')} — those projects move to {clientOf(g)}.</p>
                  ) : null}
                </div>
              )
            })}
            <p className="text-xs text-muted-foreground">{groups.length - live.length} client{groups.length - live.length === 1 ? '' : 's'} with nothing ticked will be skipped.</p>
          </div>
        ) : (
          <div className={cn('grid max-h-[60vh] gap-3 overflow-y-auto pr-1', preview.isFetching && 'opacity-60')}>
            {groups.map((g) => {
              const ids = idsOf(g)
              const on = ids.filter((id) => !excluded.has(id)).length
              const others = groups.filter((x) => x.key !== g.key)
              const suggestion = !g.joined.length && !dismissed.has(g.key) ? others.find((o) => looksSame(clientOf(g), clientOf(o))) : undefined
              return (
                <section key={g.key} className={cn('rounded-lg border p-3', on ? 'border-border' : 'border-dashed border-border opacity-70')}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Checkbox
                      aria-label={`Move ${g.name}`}
                      checked={on === ids.length ? true : on === 0 ? false : 'indeterminate'}
                      onCheckedChange={(v) => toggle(ids, v === true)}
                    />
                    {g.existingId ? (
                      <span className="font-medium">{g.name}</span>
                    ) : (
                      <Input aria-label="Client Project name" className="h-8 w-72" value={names[g.key] ?? g.name} onChange={(e) => setNames((n) => ({ ...n, [g.key]: e.target.value }))} />
                    )}
                    <span className="text-xs text-muted-foreground">{g.existingId ? 'Already a Client Project' : `${g.mainId ? 'From an existing project' : 'New'} · ${PHASE_LABEL[g.phase]}`}</span>
                    <div className="ml-auto flex items-center gap-1.5 text-xs">
                      <span className="text-muted-foreground">Same client as</span>
                      <Select value={OWN} onValueChange={(v) => v !== OWN && join(g.key, v)}>
                        <SelectTrigger className="h-8 w-56 text-xs" aria-label={`Is ${clientOf(g)} the same client as another?`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={OWN}>No — its own client</SelectItem>
                          {others.map((o) => <SelectItem key={o.key} value={o.key}>{o.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {!g.existingId && g.candidates.length ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2 pl-6 text-xs">
                      <span className="text-muted-foreground">Main Client Project</span>
                      <Select value={g.mainId ?? NEW} onValueChange={(v) => setMain((m) => ({ ...m, [g.key]: v }))}>
                        <SelectTrigger className="h-8 w-72 text-xs" aria-label={`Main Client Project for ${g.name}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NEW}>Open a new Client Project</SelectItem>
                          {g.candidates.map((c) => <SelectItem key={c.id} value={c.id}>Make “{c.name}” the Client Project ({PROJECT_TYPE_LABEL[c.from] ?? c.from})</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <span className="text-muted-foreground">{g.mainId ? 'It is renamed and keeps its work items; the phase follows its type.' : 'A new one is opened; the projects below become its Additional Requirements.'}</span>
                    </div>
                  ) : null}

                  {suggestion ? (
                    <div role="note" className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-warning-soft px-2.5 py-1.5 text-xs text-warning">
                      <AlertTriangle className="size-3.5 shrink-0" />
                      <span className="flex-1">“{clientOf(g)}” may be the same client as “{clientOf(suggestion)}”. Is it?</span>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => join(g.key, suggestion.key)}>Yes, same client</Button>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setDismissed((d) => new Set(d).add(g.key))}>No, different</Button>
                    </div>
                  ) : null}

                  {g.joined.length ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-brand-softer px-2.5 py-1.5 text-xs">
                      <Link2 className="size-3.5 shrink-0 text-brand" />
                      <span className="flex-1">
                        Joined as the same client: {g.joined.map((k) => g.convert.find((c) => `${c.clientId}|${c.geographyId ?? ''}` === k)?.clientName ?? 'another client').join(', ')}. Their projects move to {clientOf(g)}.
                      </span>
                      {g.joined.map((k) => (
                        <Button key={k} size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => join(k, null)}><Undo2 className="size-3" /> Undo</Button>
                      ))}
                    </div>
                  ) : null}

                  {g.mainId ? (() => {
                    const m = g.candidates.find((c) => c.id === g.mainId)
                    return m ? (
                      <div className="mt-2 flex items-center gap-2 pl-6 text-sm">
                        <Checkbox aria-label={`Move ${m.name}`} checked={!excluded.has(m.id)} onCheckedChange={(v) => toggle([m.id], v === true)} />
                        <span className="rounded-md bg-brand-softer px-1.5 py-0.5 text-tiny font-semibold uppercase tracking-wide text-brand">Main</span>
                        <span className={cn(excluded.has(m.id) && 'text-muted-foreground line-through')}>{m.name}</span>
                        <span className="text-xs text-muted-foreground">was {PROJECT_TYPE_LABEL[m.from] ?? m.from} · becomes the Client Project itself</span>
                      </div>
                    ) : null
                  })() : null}

                  {g.convert.length ? (
                    <p className="mt-2 pl-6 text-tiny font-semibold uppercase tracking-wide text-text-muted">Additional Requirements — order on the timeline</p>
                  ) : null}
                  <ul className="mt-1 grid gap-1 pl-6 text-sm">
                    {ordered(g).map((c) => {
                      const foreign = `${c.clientId}|${c.geographyId ?? ''}` !== g.key
                      return (
                        <li key={c.id} className="flex items-center gap-2">
                          <Checkbox aria-label={`Move ${c.name}`} checked={!excluded.has(c.id)} onCheckedChange={(v) => toggle([c.id], v === true)} />
                          <Input
                            aria-label={`Order of ${c.name}`}
                            type="number"
                            min={1}
                            max={g.convert.length}
                            className="h-7 w-14 px-2 text-xs"
                            disabled={excluded.has(c.id)}
                            value={order[c.id] ?? c.order}
                            onChange={(e) => {
                              const n = Math.round(Number(e.target.value))
                              if (Number.isFinite(n) && n >= 1) setOrder((o) => ({ ...o, [c.id]: Math.min(n, 999) }))
                            }}
                          />
                          <span className={cn(excluded.has(c.id) && 'text-muted-foreground line-through')}>{c.name}</span>
                          <span className="text-xs text-muted-foreground">
                            was {PROJECT_TYPE_LABEL[c.from] ?? c.from} · {day(c.startAt)} – {day(c.endAt)}{foreign ? ` · client “${c.clientName}”` : ''}
                          </span>
                          {!g.existingId ? (
                            <Button size="sm" variant="ghost" className="ml-auto h-6 px-2 text-xs text-brand" onClick={() => { toggle([c.id], true); setMain((m) => ({ ...m, [g.key]: c.id })) }}>
                              Make main
                            </Button>
                          ) : null}
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )
            })}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <span className="self-center text-xs text-muted-foreground">
            {selectedIds.length} project{selectedIds.length === 1 ? '' : 's'} ticked across {live.length} client{live.length === 1 ? '' : 's'}. Numbers set the order on the timeline; by default, oldest first.
          </span>
          <div className="flex gap-2">
            {step === 'confirm' ? (
              <>
                <Button variant="outline" onClick={() => setStep('choose')} disabled={run.isPending}>Back</Button>
                <Button disabled={run.isPending || !selectedIds.length} onClick={() => run.mutate()}>{run.isPending ? 'Moving…' : 'Confirm and move'}</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button disabled={!selectedIds.length || preview.isFetching} onClick={() => setStep('confirm')}>Review {selectedIds.length} project{selectedIds.length === 1 ? '' : 's'}</Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
